var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { fetchJsonContent, fetchJsonContentMetadata, fetchJsonMenu, } from "../api/index";
import { parseJsonResponseMetadata, parseJsonResponseIncludedElements, parseJsonTopLevelResponseMenu, } from "../cms-parser/index";
import { generateJsonMenuEndpoint, generateJsonResponseContentBaseEndpoint, generateJsonResponseContentEndpoint, } from "../drupal-url-builder/index";
import { getContentRefFieldFromConfig, getContentNonRefFieldsFromConfig, processJsonResponseForParagraphs, processStaticContentConfigForParagraphs, getStaticContentConfig, getContentMediaFieldFromConfig, getContentTaxonomyFieldFromConfig, } from "../utils/index";
/**
 * @function getDynamicContent
 * @description **fetches and processed dynamic page specific content.**
 * - in dynamic content, a new paragraph type can be added.
 * - a exising paragraph type can be removed.
 * - this requires minimum two API calls to fetch content.
 * - en is the default language.
 * - paragraph filters will not work if content ref field is not provided in node filters
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @param {object} [optionObject] `optional` object with optional parameters.
 * @param {string} [optionObject.lang] `optional` language of the content.
 * @param {boolean} [optionObject.isPreview] `optional` preview flag.
 * @param {string} [optionObject.previewId] `optional` preview id of the content.
 * @param {string} [optionObject.previewerId] `optional` user ID of the previer.
 * @param {object} [optionObject.nodeFields] `optional` node fields.
 * @param {object} [optionObject.paragraphFields] `optional` paragraph fields.
 * @param {object} [optionObject.filters] `optional` filters.
 * @param {object} [optionObject.pagination] `optional` pagination.
 * @param {string} [optionObject.pagination.offset] `optional` offset.
 * @param {string} [optionObject.pagination.limit] `optional` limit.
 * @param {string[]} [optionObject.sorting] `optional` array of sorting parameters.
 * @returns {Promise<object>} promise of object with page specific content.
 * @example
 * const contentTypeMachineName = "page";
 * const optionObject = {
 *   lang: "es",
 *   isPreview: true,
 *   previewId: "9384f41c-2ee2-4790-9fbe-e933b930424e",
 *   previewerId: "2",
 *   nodeFields: { page: ["field_alignment", "field_short_description"] },
 *   paragraphFields: { hero: ["field_hero_image", "field_hero_title"] },
 *   filters: { field_slug_value: "about-biolife/who-we-are" },
 *   pagination: { offset: "5", limit: "10" },
 *   sorting: ["-id", "title"],
 * };
 * await getDynamicContent(contentTypeMachineName, optionObject);
 */
export const getDynamicContent = (contentTypeMachineName, optionObject) => __awaiter(void 0, void 0, void 0, function* () {
    const contentMetadataApiEndpoint = yield generateJsonResponseContentBaseEndpoint(contentTypeMachineName, optionObject !== null && optionObject !== void 0 ? optionObject : {});
    const initialContentFromCMS = yield fetchJsonContentMetadata(contentMetadataApiEndpoint);
    if (!((initialContentFromCMS === null || initialContentFromCMS === void 0 ? void 0 : initialContentFromCMS.data) instanceof Array)) {
        initialContentFromCMS.data = [initialContentFromCMS === null || initialContentFromCMS === void 0 ? void 0 : initialContentFromCMS.data];
    }
    const contentRefField = yield getContentRefFieldFromConfig(contentTypeMachineName);
    let currentIndex = 0;
    let paragraphsUsedInContents = [];
    for (const _ of initialContentFromCMS === null || initialContentFromCMS === void 0 ? void 0 : initialContentFromCMS.data) {
        _;
        const paragraphsUsedInContent = processJsonResponseForParagraphs(contentRefField, initialContentFromCMS, currentIndex);
        paragraphsUsedInContents.push(...paragraphsUsedInContent);
        currentIndex++;
    }
    paragraphsUsedInContents = [...new Set(paragraphsUsedInContents)];
    const contentEndpoint = yield generateJsonResponseContentEndpoint(contentTypeMachineName, contentRefField, paragraphsUsedInContents, optionObject !== null && optionObject !== void 0 ? optionObject : {});
    const fullContentFromCMS = yield fetchJsonContent(contentEndpoint);
    const contents = yield prepareToolkitResponse(contentTypeMachineName, fullContentFromCMS);
    return contents;
});
/**
 * @function getStaticContent
 * @description **fetches and processed static page specific content.**
 * - in static content, a new paragraph type can not be added.
 * - a exising paragraph type can not be removed.
 * - this requires only one API call to fetch content.
 * - en is the default language.
 * - paragraph filters will not work if content ref field is not provided in node filters
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @param {object} [optionObject] `optional` object with optional parameters.
 * @param {string} [optionObject.lang] `optional` language of the content.
 * @param {boolean} [optionObject.isPreview] `optional` preview flag.
 * @param {string} [optionObject.previewId] `optional` preview id of the content.
 * @param {string} [optionObject.previewerId] `optional` user ID of the previer.
 * @param {object} [optionObject.nodeFields] `optional` node fields.
 * @param {object} [optionObject.paragraphFields] `optional` paragraph fields.
 * @param {object} [optionObject.filters] `optional` filters.
 * @param {object} [optionObject.pagination] `optional` pagination.
 * @param {string} [optionObject.pagination.offset] `optional` offset.
 * @param {string} [optionObject.pagination.limit] `optional` limit.
 * @param {string[]} [optionObject.sorting] `optional` array of sorting parameters.
 * @returns {Promise<object>} promise of object with page specific content.
 * @example
 * const contentTypeMachineName = "page";
 * const optionObject = {
 *   lang: "es",
 *   isPreview: true,
 *   previewId: "9384f41c-2ee2-4790-9fbe-e933b930424e",
 *   previewerId: "2",
 *   nodeFields: { page: ["field_alignment", "field_short_description"] },
 *   paragraphFields: { hero: ["field_hero_image", "field_hero_title"] },
 *   filters: { field_slug_value: "about-biolife/who-we-are" },
 *   pagination: { offset: "5", limit: "10" },
 *   sorting: ["-id", "title"],
 * };
 * await getStaticContent(contentTypeMachineName, optionObject);
 */
export const getStaticContent = (contentTypeMachineName, optionObject) => __awaiter(void 0, void 0, void 0, function* () {
    const staticContentConfig = yield getStaticContentConfig();
    const contentRefField = yield getContentRefFieldFromConfig(contentTypeMachineName);
    const paragraphsUsedInContent = processStaticContentConfigForParagraphs(contentTypeMachineName, staticContentConfig);
    const contentEndpoint = yield generateJsonResponseContentEndpoint(contentTypeMachineName, contentRefField, paragraphsUsedInContent, optionObject !== null && optionObject !== void 0 ? optionObject : {});
    const fullContentFromCMS = yield fetchJsonContent(contentEndpoint);
    if (!((fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.data) instanceof Array)) {
        fullContentFromCMS.data = [fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.data];
    }
    let currentIndex = 0;
    let paragraphsUsedInContents = [];
    for (const _ of fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.data) {
        _;
        const paragraphsUsedInContent = processJsonResponseForParagraphs(contentRefField, fullContentFromCMS, currentIndex);
        paragraphsUsedInContents.push(...paragraphsUsedInContent);
        currentIndex++;
    }
    paragraphsUsedInContents = [...new Set(paragraphsUsedInContents)];
    const contents = yield prepareToolkitResponse(contentTypeMachineName, fullContentFromCMS);
    return contents;
});
/**
 * @function getMinStaticContent
 * @description **fetces and processed static page specific content without include parameters.**
 * - in static content, a new paragraph type can not be added.
 * - an exising paragraph type can not be removed.
 * - this requires only one API call to fetch content.
 * - include parameters are confugured at CMS level.
 * - include parameters are required to fetch content with media in a single request.
 * - en is the default language.
 * - paragraph filters will not work if content ref field is not provided in node filters
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @param {object} [optionObject] `optional` object with optional parameters.
 * @param {string} [optionObject.lang] `optional` language of the content.
 * @param {boolean} [optionObject.isPreview] `optional` preview flag.
 * @param {string} [optionObject.previewId] `optional` preview id of the content.
 * @param {string} [optionObject.previewerId] `optional` user ID of the previer.
 * @param {object} [optionObject.nodeFields] `optional` node fields.
 * @param {object} [optionObject.paragraphFields] `optional` paragraph fields.
 * @param {object} [optionObject.filters] `optional` filters.
 * @param {object} [optionObject.pagination] `optional` pagination.
 * @param {string} [optionObject.pagination.offset] `optional` offset.
 * @param {string} [optionObject.pagination.limit] `optional` limit.
 * @param {string[]} [optionObject.sorting] `optional` array of sorting parameters.
 * @returns {Promise<object>} promise of object with page specific content.
 * @example
 * const contentTypeMachineName = "page";
 * const optionObject = {
 *   lang: "es",
 *   isPreview: true,
 *   previewId: "9384f41c-2ee2-4790-9fbe-e933b930424e",
 *   previewerId: "2",
 *   nodeFields: { page: ["field_alignment", "field_short_description"] },
 *   paragraphFields: { hero: ["field_hero_image", "field_hero_title"] },
 *   filters: { field_slug_value: "about-biolife/who-we-are" },
 *   pagination: { offset: "5", limit: "10" },
 *   sorting: ["-id", "title"],
 * };
 * await getMinStaticContent(contentTypeMachineName, optionObject);
 */
export const getMinStaticContent = (contentTypeMachineName, optionObject) => __awaiter(void 0, void 0, void 0, function* () {
    const contentEndpoint = yield generateJsonResponseContentBaseEndpoint(contentTypeMachineName, optionObject !== null && optionObject !== void 0 ? optionObject : {});
    const fullContentFromCMS = yield fetchJsonContent(contentEndpoint);
    const contents = yield prepareToolkitResponse(contentTypeMachineName, fullContentFromCMS);
    return contents;
});
/**
 * @function getMenu
 * @description **fetces and processed menu.**
 * - this requires only one API call to fetch menu.
 * - the JSON module is not used to fetch menu.
 * - en is the default language.
 * @param {string} menuMachineName - machine name of the menu.
 * @param {string} [lang] `optional` language of the menu.
 * @returns {Promise<object[]>} promise of array of object of menu.
 * @example
 * const menuMachineName = "main";
 * const lang = "es";
 * await getMenu(menuMachineName, lang);
 */
export const getMenu = (menuMachineName, lang) => __awaiter(void 0, void 0, void 0, function* () {
    const menuEndpoint = yield generateJsonMenuEndpoint(menuMachineName, lang);
    const menuJsonFromDrupal = yield fetchJsonMenu(menuEndpoint);
    const parsedMenu = parseJsonTopLevelResponseMenu(menuJsonFromDrupal);
    return parsedMenu;
});
/**
 * @function prepareToolkitResponse
 * @description **prepares the response object for the toolkit.**
 * - this is a helper function to prepare the response object for the toolkit.
 * - this is used by all the content extraction functions exposed in the toolkit.
 * @param {string} contentTypeMachineName - machine name of the content type.
 * @param {object} fullContentFromCMS  - full content from CMS.
 * @returns {Promise<object>} promise of object with page specific content.
 */
export const prepareToolkitResponse = (contentTypeMachineName, fullContentFromCMS) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const contents = [];
    const contentNonRefFields = yield getContentNonRefFieldsFromConfig(contentTypeMachineName);
    const contentMediaFields = yield getContentMediaFieldFromConfig(contentTypeMachineName);
    const contentTaxonomyFields = yield getContentTaxonomyFieldFromConfig(contentTypeMachineName);
    if (!((fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.data) instanceof Array)) {
        fullContentFromCMS.data = [fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.data];
    }
    const contentRefField = yield getContentRefFieldFromConfig(contentTypeMachineName);
    let currentIndex = 0;
    for (const _ of fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.data) {
        _;
        const paragraphsUsedInContent = processJsonResponseForParagraphs(contentRefField, fullContentFromCMS, currentIndex);
        const parsedContentPageData = yield parseJsonResponseMetadata(fullContentFromCMS, currentIndex, contentNonRefFields, contentMediaFields, contentTaxonomyFields);
        const included = (_a = fullContentFromCMS === null || fullContentFromCMS === void 0 ? void 0 : fullContentFromCMS.included) !== null && _a !== void 0 ? _a : [];
        const parsedContentParagraphs = yield parseJsonResponseIncludedElements(included, paragraphsUsedInContent);
        contents.push({
            metadata: parsedContentPageData,
            contents: parsedContentParagraphs,
        });
        currentIndex++;
    }
    return contents;
});
